/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useState } from 'react';
import { ModalAwareScrollView } from '../../shared/modals/ModalAwareScrollView';
import { Modal } from '../../shared/modals/Modal';
import {
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import type { CashOutflowRow } from '../infra/cashOutflowRepo';
import { CASH_MOVEMENT_CATEGORY_LABELS } from '../infra/cashOutflowCategories';
import { useModalBackdropPadding } from '../../shared/ui/hooks-ui/useModalBackdropPadding';
import { formatLocalDateTime } from '@shared/vueltoDateTime';
import { spacing } from '../../shared/theme/spacing'
import { typography } from '../../shared/theme/typography'
import { webOperationalScrollBindings } from '../../shared/theme/webScrollbar';
import { ticketColors } from '../../shared/theme/ticketColors';
import { cashPanelStyles as panelStyles } from './workspace/cashManagement.panelStyles';
import { formatMoney } from './workspace/cashManagement.utils';
import { settingsStyles as styles } from '../../settings/screens/settingsScreen.styles';
import { ModalFormField } from '../../shared/modals/ModalFormField'
import { ModalScreenHeader } from '../../shared/modals/ModalScreenHeader'
import { ModalHeroHeader } from '../../shared/modals/ModalHeroHeader'
import { ModalStatePanel } from '../../shared/modals/ModalStatePanel'
import { modalExperience } from '../../shared/modals/modalExperience.styles';
import { modalShellShared } from '../../shared/modals/modalShellShared.styles';
import { posSharedUi as posUi } from '../../shared/ui/posSharedUi';
import type { ActiveCashSessionInfo } from '../../expenses/components/expensePaymentFlow';
import type { ExpenseRegisterSubmitInput } from '../../expenses/screens/expenseRegisterSubmitTypes';
import type { useCashOutflows } from './hooks/useCashOutflows';

type OutflowsVm = ReturnType<typeof useCashOutflows>;

type Props = {
  vm: OutflowsVm;
  canEdit: boolean;
  canVoid: boolean;
  sessionOutflowsTotal: number;
  operatorLabel: (userId: string | null | undefined) => string | null;
  layoutWide?: boolean;
  expensesModuleEnabled: boolean;
  expenseRegisterSuppliers: Array<{ id: string; name: string }>;
  activeCashSessionInfo: ActiveCashSessionInfo | null;
  expenseRegisterSubmitting: boolean;
  onExpenseRegisterSubmit: (input: ExpenseRegisterSubmitInput) => void;
  onOpenRegister: () => void;
  /** Registrar ingreso manual (direction='in'). */
  onOpenRegisterInflow?: () => void;
  /** Solo total + CTA; lista completa en modal. */
  compactList?: boolean;
  /** La franja superior ya expone el CTA (workspace desktop). */
  hideRegisterButton?: boolean;
  /** Dentro de un modal/sheet con título propio — oculta encabezado duplicado. */
  embeddedInModal?: boolean;
  /** Primera sección del tab (sin borde superior). */
  leadSection?: boolean;
};

export function CashOutflowsSection({
  vm,
  canEdit,
  canVoid,
  sessionOutflowsTotal,
  operatorLabel,
  layoutWide,
  expensesModuleEnabled,
  expenseRegisterSuppliers,
  activeCashSessionInfo,
  expenseRegisterSubmitting,
  onExpenseRegisterSubmit,
  onOpenRegister,
  onOpenRegisterInflow,
  compactList,
  hideRegisterButton,
  embeddedInModal,
  leadSection,
}: Props) {
  const [voidReason, setVoidReason] = useState('');
  const [listModalOpen, setListModalOpen] = useState(false);
  const voidBackdropPadding = useModalBackdropPadding();
  const listBackdropPadding = useModalBackdropPadding();

  const outflowRows = vm.outflows.filter((r) => r.direction !== 'in');
  const inflowRows = vm.outflows.filter((r) => r.direction === 'in');
  const inflowsTotal = inflowRows.reduce((sum, r) => sum + r.amount, 0);

  const renderRows = (rows: CashOutflowRow[]) => (
    <View style={modalExperience.listContainer}>
      {rows.map((row, index) => (
        <View
          key={row.id}
          style={[
            localStyles.listRowWrap,
            index === rows.length - 1 && localStyles.listRowWrapLast,
          ]}
        >
          <OutflowRow
            row={row}
            canVoid={canVoid}
            layoutWide={layoutWide}
            operatorLabel={operatorLabel}
            onVoid={() => vm.handleVoidPress(row)}
          />
        </View>
      ))}
    </View>
  );

  const outflowsList = vm.loading ? (
    <ModalStatePanel loading loadingMessage="Cargando movimientos…" />
  ) : outflowRows.length === 0 ? (
    <ModalStatePanel empty emptyTitle="Sin egresos" emptyMessage="No hay salidas de efectivo en este turno." />
  ) : (
    renderRows(outflowRows)
  );

  const inflowsBlock = inflowRows.length > 0 ? (
    <View style={localStyles.inflowBlock}>
      <View style={localStyles.headerRow}>
        <Text style={panelStyles.sectionLabel}>Ingresos del turno</Text>
        <Text style={[localStyles.total, { color: ticketColors.ok }]}>{formatMoney(inflowsTotal)}</Text>
      </View>
      {renderRows(inflowRows)}
    </View>
  ) : null;

  const voidIsInflow = vm.voidTarget?.direction === 'in';

  return (
    <>
      <View style={[localStyles.section, leadSection && localStyles.sectionLead]}>
        {!embeddedInModal && !leadSection ? (
          <View style={localStyles.headerRow}>
            <Text style={panelStyles.sectionLabel}>Egresos del turno</Text>
            <Text style={localStyles.total}>{formatMoney(sessionOutflowsTotal)}</Text>
          </View>
        ) : leadSection && !embeddedInModal ? (
          <Text style={localStyles.total}>{formatMoney(sessionOutflowsTotal)}</Text>
        ) : null}
        {canEdit && !hideRegisterButton ? (
          <View style={localStyles.registerActions}>
            <TouchableOpacity style={styles.primaryButton} onPress={onOpenRegister} accessibilityRole="button" testID="caja.egresos.register">
              <Text style={styles.primaryButtonText}>Registrar egreso</Text>
            </TouchableOpacity>
            {onOpenRegisterInflow ? (
              <TouchableOpacity style={styles.secondaryButton} onPress={onOpenRegisterInflow} accessibilityRole="button" testID="caja.ingresos.register">
                <Text style={styles.secondaryButtonText}>Registrar ingreso</Text>
              </TouchableOpacity>
            ) : null}
          </View>
        ) : null}

        {compactList && !embeddedInModal ? (
          outflowRows.length > 0 || inflowRows.length > 0 ? (
            <TouchableOpacity
              style={localStyles.compactListBtn}
              onPress={() => setListModalOpen(true)}
              accessibilityRole="button"
              testID="caja.egresos.view-list"
            >
              <Text style={localStyles.compactListBtnText}>
                Ver {outflowRows.length} {outflowRows.length === 1 ? 'egreso' : 'egresos'}
                {inflowRows.length > 0
                  ? ` · ${inflowRows.length} ${inflowRows.length === 1 ? 'ingreso' : 'ingresos'}`
                  : ''}
              </Text>
              <Text style={styles.caption}>Detalle y anulaciones</Text>
            </TouchableOpacity>
          ) : (
            <Text style={[styles.caption, { marginTop: spacing.sm }]}>Sin movimientos en este turno.</Text>
          )
        ) : (
          <>
            {outflowsList}
            {inflowsBlock}
          </>
        )}
      </View>

      {compactList ? (
        <Modal visible={listModalOpen} animationType="fade" transparent onRequestClose={() => setListModalOpen(false)}>
          <View style={[posUi.modalBackdrop, modalExperience.modalRoot, listBackdropPadding]}>
            <View
              style={[
                posUi.modalCard,
                modalShellShared.modalCardShell,
                modalExperience.modalWidthStandard,
                localStyles.modalCard,
              ]}
            >
              <ModalScreenHeader
                title={inflowRows.length > 0 ? 'Movimientos del turno' : 'Egresos del turno'}
                subtitle={`${outflowRows.length} ${outflowRows.length === 1 ? 'egreso' : 'egresos'}${inflowRows.length > 0 ? ` · ${inflowRows.length} ${inflowRows.length === 1 ? 'ingreso' : 'ingresos'}` : ''} registrados`}
                onClose={() => setListModalOpen(false)}
              />
              <ModalHeroHeader kicker="Total egresado" title={formatMoney(sessionOutflowsTotal)} />
              <ModalAwareScrollView
                {...webOperationalScrollBindings({ flexShrink: 1 })}
                keyboardShouldPersistTaps="handled"
                bottomOffset={spacing.sm}
              >
                {outflowsList}
                {inflowsBlock}
              </ModalAwareScrollView>
              <TouchableOpacity style={styles.secondaryButton} onPress={() => setListModalOpen(false)} accessibilityRole="button">
                <Text style={styles.secondaryButtonText}>Listo</Text>
              </TouchableOpacity>
            </View>
          </View>
        </Modal>
      ) : null}

      <Modal
        visible={vm.voidTarget != null}
        animationType="fade"
        transparent
        onRequestClose={() => vm.setVoidTarget(null)}
      >
        <View style={[posUi.modalBackdrop, modalExperience.modalRoot, voidBackdropPadding]}>
          <View
            style={[
              posUi.modalCard,
              modalShellShared.modalCardShell,
              modalExperience.modalWidthStandard,
              localStyles.modalCard,
            ]}
          >
            <ModalScreenHeader title={voidIsInflow ? 'Anular ingreso' : 'Anular egreso'} onClose={() => vm.setVoidTarget(null)} />
            <ModalHeroHeader
              kicker="Confirmación"
              subtitle="Decí por qué anulás. Mínimo 4 caracteres."
            />
            <View style={modalExperience.formPanel}>
              <ModalFormField label="Motivo" required>
                <TextInput
                  value={voidReason}
                  onChangeText={setVoidReason}
                  placeholder="Motivo"
                  placeholderTextColor={ticketColors.ink3}
                  style={[modalExperience.formInput, modalExperience.formInputMultiline]}
                  multiline
                  editable={!vm.submitting}
                  testID="caja.egresos.void-reason"
                />
              </ModalFormField>
            </View>
            <View style={localStyles.actions}>
              <TouchableOpacity
                style={[styles.secondaryButton, localStyles.actionBtn]}
                onPress={() => {
                  setVoidReason('');
                  vm.setVoidTarget(null);
                }}
                disabled={vm.submitting}
                accessibilityRole="button"
              >
                <Text style={styles.secondaryButtonText}>Cancelar</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.primaryButton, localStyles.actionBtn, { backgroundColor: ticketColors.bad }]}
                onPress={() => {
                  vm.confirmVoid(voidReason);
                  setVoidReason('');
                }}
                disabled={vm.submitting}
                accessibilityRole="button"
                testID="caja.egresos.void-confirm"
              >
                <Text style={styles.primaryButtonText}>{vm.submitting ? 'Anulando…' : 'Anular'}</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
    </>
  );
}

function OutflowRow({
  row,
  canVoid,
  layoutWide,
  operatorLabel,
  onVoid,
}: {
  row: CashOutflowRow;
  canVoid: boolean;
  layoutWide?: boolean;
  operatorLabel: (userId: string | null | undefined) => string | null;
  onVoid: () => void;
}) {
  const isInflow = row.direction === 'in';
  const voided = row.status === 'voided';
  const categoryLabel = CASH_MOVEMENT_CATEGORY_LABELS[row.category] ?? row.category;
  const createdByLabel = operatorLabel(row.createdBy);
  const voidedByLabel = operatorLabel(row.voidedBy);
  const struck = voided ? localStyles.struck : null;

  const voidMeta = voided
    ? `Anulado${row.voidReason?.trim() ? `: "${row.voidReason.trim()}"` : ''}${
        voidedByLabel ? ` — Por ${voidedByLabel}` : ''
      }${row.voidedAt ? ` · ${formatLocalDateTime(row.voidedAt)}` : ''}`
    : null;

  return (
    <View style={[localStyles.row, layoutWide && localStyles.rowWide]}>
      {/* Thumb de dirección — proto .thumb.in/.out (↑ rojo egreso / ↓ verde ingreso). */}
      <View
        style={[
          localStyles.directionThumb,
          isInflow ? localStyles.directionThumbIn : localStyles.directionThumbOut,
        ]}
      >
        <Ionicons
          name={isInflow ? 'arrow-down' : 'arrow-up'}
          size={15}
          color={isInflow ? ticketColors.ok : ticketColors.bad}
        />
      </View>
      <View style={localStyles.rowMain}>
        <Text style={[modalExperience.listRowTitle, struck]}>{categoryLabel}</Text>
        <Text style={[modalExperience.listMeta, struck]}>
          {formatLocalDateTime(row.createdAt)}
          {createdByLabel ? ` · Por ${createdByLabel}` : ''}
        </Text>
        {row.note?.trim() && !voided ? (
          <Text style={modalExperience.listMetaMuted} numberOfLines={2}>
            {row.note.trim()}
          </Text>
        ) : null}
        {voidMeta ? (
          <Text style={modalExperience.listMetaMuted} numberOfLines={2}>
            {voidMeta}
          </Text>
        ) : null}
      </View>
      <View style={localStyles.rowAside}>
        <Text
          style={[
            localStyles.rowAmount,
            isInflow && !voided && { color: ticketColors.ok },
            struck,
          ]}
        >
          {isInflow ? '+' : ''}{formatMoney(row.amount)}
        </Text>
        {voided ? (
          <View style={localStyles.voidedPill}>
            <Text style={localStyles.voidedPillText}>Anulado</Text>
          </View>
        ) : canVoid ? (
          <TouchableOpacity onPress={onVoid} accessibilityRole="button" accessibilityLabel={isInflow ? 'Anular ingreso' : 'Anular egreso'} testID="caja.egresos.void">
            <Text style={localStyles.voidAction}>Anular</Text>
          </TouchableOpacity>
        ) : null}
      </View>
    </View>
  );
}

const localStyles = StyleSheet.create({
  section: {
    marginTop: spacing.md,
    paddingTop: spacing.md,
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: ticketColors.rule,
    gap: spacing.sm,
  },
  sectionLead: {
    marginTop: 0,
    paddingTop: 0,
    borderTopWidth: 0,
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.sm,
  },
  inflowBlock: {
    marginTop: spacing.sm,
    gap: spacing.xs,
  },
  registerActions: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.sm,
  },
  title: {
    ...typography.body,
    color: ticketColors.ink,
    fontWeight: '600',
  },
  total: {
    ...typography.body,
    color: ticketColors.bad,
    fontWeight: '700',
  },
  listRowWrap: {
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: ticketColors.rule,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
  },
  listRowWrapLast: {
    borderBottomWidth: 0,
  },
  compactListBtn: {
    marginTop: spacing.sm,
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.md,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.paper2,
    gap: 2,
  },
  compactListBtnText: {
    ...typography.body,
    color: ticketColors.gold,
    fontWeight: '700',
  },
  row: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: spacing.sm,
  },
  rowWide: {
    paddingHorizontal: spacing.xs,
  },
  // Thumb de dirección — proto .thumb.in/.out de la vista Egresos.
  directionThumb: {
    width: 32,
    height: 32,
    borderRadius: 10,
    borderWidth: 1,
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 1,
  },
  directionThumbOut: {
    backgroundColor: ticketColors.bad + '14',
    borderColor: ticketColors.bad + '40',
  },
  directionThumbIn: {
    backgroundColor: ticketColors.ok + '14',
    borderColor: ticketColors.ok + '40',
  },
  // Fila anulada — proto .lrow.struck: texto tachado + opacidad.
  struck: {
    textDecorationLine: 'line-through',
    opacity: 0.5,
  },
  // Pill muted "Anulado" — proto .pill.muted.
  voidedPill: {
    alignSelf: 'flex-end',
    backgroundColor: ticketColors.tile,
    borderRadius: 999,
    paddingHorizontal: 8,
    paddingVertical: 3,
  },
  voidedPillText: {
    ...typography.caption,
    fontSize: 11,
    fontWeight: '700',
    color: ticketColors.ink3,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
  },
  rowMain: {
    flex: 1,
    minWidth: 0,
  },
  rowAside: {
    alignItems: 'flex-end',
    gap: spacing.xs,
  },
  rowTitle: {
    ...typography.body,
    color: ticketColors.ink,
    fontWeight: '600',
  },
  rowMeta: {
    ...typography.caption,
    color: ticketColors.ink2,
    marginTop: 2,
  },
  rowNote: {
    ...typography.caption,
    color: ticketColors.ink2,
    marginTop: 2,
  },
  rowAmount: {
    ...typography.body,
    color: ticketColors.ink,
    fontWeight: '700',
  },
  voidAction: {
    ...typography.caption,
    color: ticketColors.bad,
    fontWeight: '700',
  },
  modalCard: {
    maxHeight: '92%',
    gap: spacing.sm,
  },
  actions: {
    flexDirection: 'row',
    gap: spacing.sm,
    marginTop: spacing.sm,
  },
  actionBtn: {
    flex: 1,
  },
});
